import React, { useState, useEffect } from 'react';
import {
  Shield, AlertTriangle, Key, Users, Database, Globe,
  ChevronDown, ChevronUp, Loader2, RefreshCw, Search
} from 'lucide-react';
import { osint as osintApi, scans as scansApi } from '../api/client';
import { toast } from '../utils/toastUtils';

// OSINT context badge for credential-related vulnerabilities
export const OSINTContextBadge = ({ vulnerability }) => {
  const osintEnrichment = vulnerability?.metadata?.osint_enrichment;

  if (!osintEnrichment) return null;

  const isCredentialRelated = osintEnrichment.osint_context?.credential_related;
  const authService = osintEnrichment.osint_context?.auth_service;
  const recommendations = osintEnrichment.recommendations || [];

  return (
    <div className="flex items-center gap-2">
      {isCredentialRelated && (
        <span className="px-2 py-1 bg-purple-500/20 text-purple-400 border border-purple-500/30 rounded text-xs flex items-center gap-1">
          <Key className="w-3 h-3" />
          OSINT
        </span>
      )}
      {authService && (
        <span className="px-2 py-1 bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 rounded text-xs">
          {authService.toUpperCase()}
        </span>
      )}
    </div>
  );
};

// Main OSINT insights panel for vulnerability page
const VulnerabilityOSINTInsights = ({ workspaceId }) => {
  const [loading, setLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const [recentScans, setRecentScans] = useState([]);
  const [selectedScanId, setSelectedScanId] = useState(null);
  const [osintStats, setOsintStats] = useState(null);

  useEffect(() => {
    loadRecentScans();
  }, [workspaceId]);

  useEffect(() => {
    if (selectedScanId) {
      loadOSINTStats();
    }
  }, [selectedScanId]);

  const loadRecentScans = async () => {
    try {
      const response = await scansApi.list(0, 10, workspaceId);
      const scans = response.scans || response || [];
      const completedScans = scans.filter(s => s.status === 'completed' || s.status === 'finished');
      setRecentScans(completedScans);

      if (completedScans.length > 0 && !selectedScanId) {
        setSelectedScanId(completedScans[0].id);
      }
    } catch (error) {
      console.error('Failed to load scans:', error);
    }
  };

  const loadOSINTStats = async () => {
    setLoading(true);
    try {
      const enrichment = await osintApi.getScanEnrichment(selectedScanId);

      if (enrichment.enriched) {
        // Calculate OSINT vulnerability stats
        const summary = enrichment.summary || {};
        const byType = enrichment.by_data_type || {};

        // Count credential-related findings
        let breachedCredentials = 0;
        let highRiskAccounts = 0;

        Object.values(byType).forEach(typeData => {
          (typeData.credentials || []).forEach(cred => {
            const hibp = cred.osint_results?.hibp || {};
            if (hibp.breach_count > 0 || hibp.breaches?.length > 0) {
              breachedCredentials++;
            }
            if ((cred.risk_score || 0) >= 50) {
              highRiskAccounts++;
            }
          });
        });

        setOsintStats({
          total_credentials: summary.total_credentials || 0,
          enriched: summary.enriched || 0,
          high_risk: summary.high_risk || 0,
          breached_credentials: breachedCredentials,
          high_risk_accounts: highRiskAccounts,
          data_types: Object.keys(byType),
          last_enriched: summary.enriched_at
        });
      } else {
        setOsintStats(null);
      }
    } catch (error) {
      console.error('Failed to load OSINT stats:', error);
      setOsintStats(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-slate-800/30 border border-slate-700 rounded-xl overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-4 flex items-center justify-between hover:bg-slate-800/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-purple-500/10">
            <Shield className="w-5 h-5 text-purple-400" />
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-white">OSINT & Breach Intelligence</h3>
            <p className="text-sm text-gray-400">
              Contexte OSINT pour les vulnérabilités liées aux credentials
            </p>
          </div>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-400" />
        )}
      </button>

      {/* Content */}
      {isExpanded && (
        <div className="p-4 pt-0 space-y-4">
          {/* Scan selector */}
          <div className="flex items-center gap-3">
            <select
              value={selectedScanId || ''}
              onChange={(e) => setSelectedScanId(e.target.value || null)}
              className="flex-1 bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white"
            >
              <option value="">-- Selectionner un scan --</option>
              {recentScans.map(scan => (
                <option key={scan.id} value={scan.id}>
                  {scan.name || scan.target || `Scan ${scan.id.slice(0, 8)}`}
                  {' - '}
                  {new Date(scan.created_at).toLocaleDateString('fr-FR')}
                </option>
              ))}
            </select>
            <button
              onClick={loadOSINTStats}
              disabled={!selectedScanId || loading}
              className="p-2 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 rounded-lg transition-colors"
              title="Rafraichir"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
              ) : (
                <RefreshCw className="w-5 h-5 text-gray-400" />
              )}
            </button>
          </div>

          {/* Stats */}
          {osintStats ? (
            <div className="space-y-4">
              {/* Quick stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-slate-900/50 rounded-lg p-3 text-center">
                  <p className="text-xl font-bold text-white">{osintStats.total_credentials}</p>
                  <p className="text-xs text-gray-400">Credentials</p>
                </div>
                <div className="bg-slate-900/50 rounded-lg p-3 text-center">
                  <p className="text-xl font-bold text-green-400">{osintStats.enriched}</p>
                  <p className="text-xs text-gray-400">Enrichis</p>
                </div>
                <div className="bg-red-500/10 rounded-lg p-3 text-center border border-red-500/30">
                  <p className="text-xl font-bold text-red-400">{osintStats.breached_credentials}</p>
                  <p className="text-xs text-gray-400">Breaches</p>
                </div>
                <div className="bg-yellow-500/10 rounded-lg p-3 text-center border border-yellow-500/30">
                  <p className="text-xl font-bold text-yellow-400">{osintStats.high_risk_accounts}</p>
                  <p className="text-xs text-gray-400">Haut risque</p>
                </div>
              </div>

              {/* Data types found */}
              {osintStats.data_types.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-gray-300 mb-2">Types de donnees detectes:</p>
                  <div className="flex flex-wrap gap-2">
                    {osintStats.data_types.map(type => (
                      <span
                        key={type}
                        className="px-2 py-1 bg-slate-700 text-gray-300 rounded text-xs capitalize"
                      >
                        {type.replace('_', ' ')}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Recommendations */}
              {osintStats.breached_credentials > 0 && (
                <div className="bg-red-500/5 border border-red-500/20 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-red-400">Credentials compromis détectés</p>
                      <p className="text-xs text-gray-400 mt-1">
                        {osintStats.breached_credentials} credential(s) trouvé(s) dans des breaches connues.
                        Vérifiez la page Credentials pour plus de détails et les recommandations de remédiation.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* High risk accounts warning */}
              {osintStats.high_risk_accounts > 0 && (
                <div className="bg-yellow-500/5 border border-yellow-500/20 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <Users className="w-4 h-4 text-yellow-400 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-yellow-400">Comptes a haut risque</p>
                      <p className="text-xs text-gray-400 mt-1">
                        {osintStats.high_risk_accounts} compte(s) avec un score de risque eleve.
                        Ces comptes peuvent etre des cibles prioritaires pour les attaquants.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {osintStats.last_enriched && (
                <p className="text-xs text-gray-500">
                  Dernier enrichissement: {new Date(osintStats.last_enriched).toLocaleString('fr-FR')}
                </p>
              )}
            </div>
          ) : (
            <div className="text-center py-6">
              {selectedScanId ? (
                <>
                  <Database className="w-10 h-10 text-gray-500 mx-auto mb-3" />
                  <p className="text-gray-400 text-sm">Aucun enrichissement OSINT pour ce scan</p>
                  <p className="text-gray-500 text-xs mt-1">
                    Lancez l'enrichissement depuis la page Credentials
                  </p>
                </>
              ) : (
                <>
                  <Search className="w-10 h-10 text-gray-500 mx-auto mb-3" />
                  <p className="text-gray-400 text-sm">Sélectionnez un scan pour voir les données OSINT</p>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VulnerabilityOSINTInsights;
